<template>
    <div
        class="relative inline-block w-full"
        :class="alwaysOpen ? 'h-104' : ''"
    >
        <!-- Input Field -->
        <div class="relative">
            <input
                ref="input"
                type="text"
                :value="formattedDateRange"
                :placeholder="placeholder"
                :disabled="disabled"
                :class="[
                    'w-full px-3 py-2 pr-10 border rounded-md shadow-sm transition bg-theme-canvas text-theme-base',
                    'focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-theme-primary',

                    'py-2',
                    disabled ? 'cursor-not-allowed' : '',
                    'border-theme-border'
                ]"
                readonly
                @click="toggleCalendar"
                @keydown.enter.prevent="toggleCalendar"
                @keydown.space.prevent="toggleCalendar"
                @keydown.esc="closeCalendar"
            />
            <!-- Calendar Icon -->
            <div
                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            >
                <CalendarIcon class="w-5 h-5 text-theme-base" />
            </div>
        </div>

        <!-- Calendar Dropdown -->
        <div
            v-if="showCalendar || alwaysOpen"
            class="absolute z-50 mt-1 bg-theme-canvas text-theme-base border border-theme-border rounded-lg shadow-lg"
            :class="dropdownPosition"
        >
            <!-- Header -->
            <div
                class="flex items-center justify-between px-4 py-3 border-b border-theme-border"
            >
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="previousMonth"
                >
                    <ChevronLeftIcon class="w-5 h-5 text-theme-base" />
                </button>
                <div class="font-semibold text-theme-base">
                    {{ monthNames[currentMonth] }} {{ currentYear }}
                </div>
                <button
                    type="button"
                    class="p-1 rounded-md hover:bg-theme-hover transition"
                    @click="nextMonth"
                >
                    <ChevronRightIcon class="w-5 h-5 text-theme-base" />
                </button>
            </div>

            <!-- Weekday Headers -->
            <div
                class="grid grid-cols-7 gap-1 px-4 py-2 text-xs font-medium text-theme-muted text-center"
            >
                <div v-for="day in weekDays" :key="day">{{ day }}</div>
            </div>

            <!-- Calendar Days -->
            <div class="grid grid-cols-7 gap-1 px-4 pb-4">
                <button
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    type="button"
                    :disabled="day.disabled"
                    :class="[
                        'w-8 h-8 text-sm rounded-md transition',
                        day.isToday ? 'border border-theme-primary' : '',
                        day.isRangeStart || day.isRangeEnd
                            ? 'bg-theme-primary text-white font-semibold hover:opacity-90'
                            : '',
                        day.isInRange && !day.isRangeStart && !day.isRangeEnd
                            ? 'bg-theme-hover text-theme-primary'
                            : '',
                        !day.isRangeStart &&
                        !day.isRangeEnd &&
                        !day.isInRange &&
                        !day.isOtherMonth &&
                        !day.disabled
                            ? 'hover:bg-theme-hover text-theme-base'
                            : '',
                        day.isOtherMonth && !day.isInRange
                            ? 'text-theme-muted'
                            : '',
                        day.disabled
                            ? 'text-theme-disabled cursor-not-allowed'
                            : 'cursor-pointer'
                    ]"
                    @click="selectDate(day)"
                >
                    {{ day.day }}
                </button>
            </div>

            <!-- Days count display -->
            <div
                v-if="showDaysCount && startDate && endDate"
                class="px-4 py-2 border-t border-theme-border text-center text-sm text-theme-primary"
            >
                <span class="font-semibold">
                    Rango: {{ daysCount }}
                    {{ daysCount === 1 ? "día" : "días" }}
                </span>
            </div>

            <!-- Footer buttons -->
            <div class="px-4 py-2 border-t border-theme-border flex gap-2">
                <button
                    type="button"
                    class="flex-1 px-3 py-1.5 text-sm text-theme-primary hover:bg-theme-hover rounded-md transition"
                    @click="selectToday"
                >
                    Hoy
                </button>
                <button
                    v-if="startDate || endDate"
                    type="button"
                    class="flex-1 px-3 py-1.5 text-sm text-theme-base hover:bg-theme-hover rounded-md transition"
                    @click="clearSelection"
                >
                    Limpiar
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";

import CalendarIcon from "@/components/icons/CalendarIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

export default {
    name: "DateRangePicker",
    components: {
        CalendarIcon,
        ChevronLeftIcon,
        ChevronRightIcon
    },
    model: {
        prop: "value",
        event: "change"
    },
    props: {
        value: {
            type: Object,
            default: () => ({})
        },
        allowFutureDates: {
            type: Boolean,
            default: false
        },
        enabledRelativeLabels: {
            type: Boolean,
            default: true
        },
        showDaysCount: {
            type: Boolean,
            default: true
        },
        defaultRange: {
            type: Number,
            default: 1
        },
        size: {
            type: String,
            default: "md",
            validator: (value) => ["sm", "md", "lg"].includes(value)
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: "Selecciona un rango de fechas"
        },
        alwaysOpen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const now = DateTime.now();
        return {
            showCalendar: false,
            currentMonth: now.month - 1,
            currentYear: now.year,
            startDate: null,
            endDate: null,
            hoverDate: null,
            minDate: "2020-01-01",
            monthNames: [
                "Enero",
                "Febrero",
                "Marzo",
                "Abril",
                "Mayo",
                "Junio",
                "Julio",
                "Agosto",
                "Septiembre",
                "Octubre",
                "Noviembre",
                "Diciembre"
            ],
            weekDays: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
        };
    },
    computed: {
        maxDate() {
            if (this.allowFutureDates) {
                return DateTime.now().plus({ years: 2 }).startOf("day");
            }
            return DateTime.now().startOf("day");
        },
        formattedDateRange() {
            if (!this.startDate && !this.endDate) return "";
            if (!this.endDate) {
                return this.formatDate(this.startDate);
            }
            return `${this.formatDate(this.startDate)} - ${this.formatDate(
                this.endDate
            )}`;
        },
        calendarDays() {
            const days = [];
            const firstDay = DateTime.fromObject({
                year: this.currentYear,
                month: this.currentMonth + 1,
                day: 1
            });
            const lastDay = firstDay.endOf("month");
            const prevLastDay = firstDay.minus({ months: 1 }).endOf("month");

            const firstDayWeek = firstDay.weekday % 7;
            const lastDate = lastDay.day;
            const prevLastDate = prevLastDay.day;

            const today = DateTime.now().startOf("day");

            // Previous month days
            for (let i = firstDayWeek - 1; i >= 0; i--) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth,
                    day: prevLastDate - i
                });
                days.push({
                    day: prevLastDate - i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isRangeStart: this.isRangeStart(date),
                    isRangeEnd: this.isRangeEnd(date),
                    isInRange: this.isInRange(date),
                    disabled: this.isDateDisabled(date)
                });
            }

            // Current month days
            for (let i = 1; i <= lastDate; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 1,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: false,
                    isToday: date.startOf("day").equals(today),
                    isRangeStart: this.isRangeStart(date),
                    isRangeEnd: this.isRangeEnd(date),
                    isInRange: this.isInRange(date),
                    disabled: this.isDateDisabled(date)
                });
            }

            // Next month days
            const remainingDays = 42 - days.length;
            for (let i = 1; i <= remainingDays; i++) {
                const date = DateTime.fromObject({
                    year: this.currentYear,
                    month: this.currentMonth + 2,
                    day: i
                });
                days.push({
                    day: i,
                    date: date,
                    isOtherMonth: true,
                    isToday: false,
                    isRangeStart: this.isRangeStart(date),
                    isRangeEnd: this.isRangeEnd(date),
                    isInRange: this.isInRange(date),
                    disabled: this.isDateDisabled(date)
                });
            }

            return days;
        },
        dropdownPosition() {
            return "left-0";
        },
        daysCount() {
            if (!this.startDate || !this.endDate) {
                return 0;
            }
            return Math.round(this.endDate.diff(this.startDate, "days").days);
        }
    },
    watch: {
        value: {
            handler(newValue) {
                if (newValue && Object.keys(newValue).length > 0) {
                    this.startDate = newValue.begin
                        ? DateTime.fromISO(newValue.begin)
                        : null;
                    this.endDate = newValue.end
                        ? DateTime.fromISO(newValue.end)
                        : null;
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {
        if (!this.value || Object.keys(this.value).length === 0) {
            this.setDefaultDates();
        }
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleCalendar() {
            if (this.disabled) return;
            this.showCalendar = !this.showCalendar;
        },
        closeCalendar() {
            this.showCalendar = false;
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.closeCalendar();
            }
        },
        previousMonth() {
            if (this.currentMonth === 0) {
                this.currentMonth = 11;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
        },
        nextMonth() {
            if (this.currentMonth === 11) {
                this.currentMonth = 0;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
        },
        selectDate(day) {
            if (day.disabled) return;

            const selectedDate = day.date.startOf("day");

            // Si no hay fecha de inicio, o si ya hay inicio y fin, empezar de nuevo
            if (!this.startDate || (this.startDate && this.endDate)) {
                this.startDate = selectedDate;
                this.endDate = null;
            } else {
                // Ya hay fecha de inicio, seleccionar fecha de fin
                if (selectedDate < this.startDate) {
                    // Si la fecha seleccionada es anterior al inicio, invertir
                    this.endDate = this.startDate;
                    this.startDate = selectedDate;
                } else {
                    this.endDate = selectedDate;
                }
                this.emitChange();
            }
        },
        selectToday() {
            const today = DateTime.now().startOf("day");
            if (this.isDateDisabled(today)) return;

            this.startDate = today;
            this.endDate = today;
            this.currentMonth = today.month - 1;
            this.currentYear = today.year;
            this.emitChange();
        },
        clearSelection() {
            this.startDate = null;
            this.endDate = null;
            this.emitChange();
        },
        setDefaultDates() {
            this.endDate = DateTime.now().startOf("day");
            this.startDate = DateTime.now()
                .plus({ days: -this.defaultRange })
                .startOf("day");
            this.emitChange();
        },
        emitChange() {
            this.$emit("change", {
                begin: this.startDate ? this.startDate.toISODate() : "",
                end: this.endDate ? this.endDate.toISODate() : ""
            });
        },
        formatDate(date) {
            if (!date) return "";

            const day = String(date.day).padStart(2, "0");
            const month = String(date.month).padStart(2, "0");
            const year = date.year;
            const absoluteDate = `${day}/${month}/${year}`;
            if (!this.enabledRelativeLabels) {
                return absoluteDate;
            }
            const relativeDate = this.getRelativeDate(date);
            if (relativeDate) {
                return `${relativeDate} (${absoluteDate})`;
            }

            return absoluteDate;
        },
        getRelativeDate(date) {
            if (!date) return null;

            const today = DateTime.now().startOf("day");
            const targetDate = date.startOf("day");
            const diffDays = Math.round(targetDate.diff(today, "days").days);
            const absDays = Math.abs(diffDays);

            if (absDays > 30) return null;

            if (diffDays === 0) return "Hoy";

            if (diffDays === 1) return "Mañana";
            if (diffDays === 2) return "Pasado mañana";

            if (diffDays === -1) return "Ayer";
            if (diffDays === -2) return "Antier";

            const dayName = targetDate.setLocale("es").toFormat("cccc");

            if (absDays >= 3 && absDays <= 7) {
                return diffDays > 0 ? `Este ${dayName}` : `${dayName} pasado`;
            }

            if (absDays >= 8 && absDays <= 14) {
                return diffDays > 0
                    ? `Próximo ${dayName}`
                    : `${dayName} de la semana pasada`;
            }

            if (absDays >= 15 && absDays <= 30) {
                const isSameMonth =
                    targetDate.month === today.month &&
                    targetDate.year === today.year;

                const isNextMonth =
                    targetDate.month === today.month + 1 ||
                    (today.month === 12 &&
                        targetDate.month === 1 &&
                        targetDate.year === today.year + 1);

                const isPrevMonth =
                    targetDate.month === today.month - 1 ||
                    (today.month === 1 &&
                        targetDate.month === 12 &&
                        targetDate.year === today.year - 1);

                if (isSameMonth) {
                    return diffDays > 0
                        ? `En ${absDays} días`
                        : `Hace ${absDays} días`;
                }

                if (isNextMonth && diffDays > 0) return "Próximo mes";
                if (isPrevMonth && diffDays < 0) return "El mes pasado";
            }

            return null;
        },
        isRangeStart(date) {
            if (!this.startDate) return false;
            return date.startOf("day").equals(this.startDate);
        },
        isRangeEnd(date) {
            if (!this.endDate) return false;
            return date.startOf("day").equals(this.endDate);
        },
        isInRange(date) {
            if (!this.startDate || !this.endDate) return false;
            const checkDate = date.startOf("day");
            return checkDate >= this.startDate && checkDate <= this.endDate;
        },
        isDateDisabled(date) {
            const compareDate = date.startOf("day");

            const minDate = DateTime.fromISO(this.minDate).startOf("day");
            if (compareDate < minDate) return true;

            if (compareDate > this.maxDate) return true;

            return false;
        }
    }
};
</script>
